import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';

// /discord/link — connect a Discord account to a StreamNook (Twitch) identity so the bot
// can grant the Subscriber + Supporter roles. Flow: sign in with Twitch (if needed) ->
// "Connect Discord" -> /api/discord/start -> Discord OAuth -> /api/discord/callback writes
// the link and lands back here with ?linked=1.

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}

function TwitchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    </svg>
  );
}

export default function DiscordLink() {
  const { me, loading, login, refresh } = useAuth();
  const [params] = useSearchParams();
  const linked = params.get('linked') === '1';
  const error = params.get('discord_error');

  useEffect(() => {
    if (linked) void refresh();
  }, [linked, refresh]);

  return (
    <div className="relative flex min-h-[72vh] items-center justify-center px-4 py-16">
      <div className="hero-aurora" />

      <div className="glass-panel w-full max-w-md p-8 text-center">
        <img
          src="/streamnook-logo.png"
          alt="StreamNook"
          className="mx-auto mb-5 h-14 w-14 object-contain"
        />

        {loading ? (
          <>
            <h1 className="mb-2 font-serif text-4xl text-textPrimary">Discord</h1>
            <p className="py-4 text-textSecondary">Loading…</p>
          </>
        ) : linked ? (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-dim text-accent">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h1 className="mb-2 font-serif text-4xl text-textPrimary">You're linked</h1>
            <p className="text-textSecondary">
              Your Discord is connected. Your Subscriber and Supporter roles stay in sync
              automatically, you can close this page.
            </p>
          </>
        ) : !me ? (
          <>
            <h1 className="mb-2 font-serif text-4xl text-textPrimary">Link your Discord</h1>
            <p className="mb-6 text-textSecondary">
              Sign in with Twitch first so we can match your StreamNook membership, then connect your
              Discord account to claim your roles.
            </p>
            <button
              onClick={login}
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-lg bg-[#9146FF] px-5 py-3 font-medium text-white transition-colors hover:bg-[#772ce8]"
            >
              <TwitchIcon className="h-5 w-5" />
              <span>
                Sign in<span className="sr-only"> with Twitch</span>
              </span>
            </button>
          </>
        ) : (
          <>
            <h1 className="mb-2 font-serif text-4xl text-textPrimary">Link your Discord</h1>
            <p className="mb-6 text-textSecondary">
              Signed in as <span className="font-medium text-textPrimary">{me.display_name}</span>.
              Connect your Discord account to claim your Subscriber and Supporter roles.
            </p>
            {error ? (
              <p className="mb-4 rounded-lg border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
                Something went wrong ({error}). Please try again.
              </p>
            ) : null}
            <a
              href="/api/discord/start"
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-lg bg-[#5865F2] px-5 py-3 font-medium text-white transition-colors hover:bg-[#4752c4]"
            >
              <DiscordIcon className="h-5 w-5" />
              Connect Discord
            </a>
            <p className="mt-4 text-xs text-textMuted">
              We only read your Discord username to grant your roles.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
